import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Car, Usb } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const finishLogin = (token) => {
    localStorage.setItem("adminToken", token);
    toast.success("Erfolgreich angemeldet");
    navigate("/admin/dashboard", { replace: true });
  };

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) return;
    window.history.replaceState(null, "", window.location.pathname);
    axios.post(`${API}/admin/redeem-code`, { code })
      .then((r) => finishLogin(r.data.token))
      .catch(() => toast.error("Anmelde-Link abgelaufen - bitte erneut aus dem Verwaltungsprogramm öffnen"));
  }, []);

  const handleKeyFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      if (data.type !== "auto-thurm-admin-key" || !data.key) throw new Error("format");
      const r = await axios.post(`${API}/admin/usb-auth`, { key: data.key });
      finishLogin(r.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Das ist keine gültige Auto-Thurm-Schlüsseldatei");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      
      // Check if it's a maintenance mode response
      if (response.data.maintenance_mode !== undefined) {
        if (response.data.maintenance_mode) {
          toast.success("Wartungsmodus AKTIVIERT - Website ist jetzt gesperrt");
        } else {
          toast.success("Wartungsmodus DEAKTIVIERT - Website ist wieder sichtbar");
        }
        setPassword("");
        return;
      }
      
      // Check for redirect (tiremap_edit or tiremap)
      if (response.data.redirect) {
        localStorage.setItem("adminToken", response.data.token);
        toast.success("Erfolgreich angemeldet");
        navigate(response.data.redirect);
        return;
      }
      
      // Normal login
      localStorage.setItem("adminToken", response.data.token);
      toast.success("Erfolgreich angemeldet");
      navigate("/admin/dashboard");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Falsches Passwort");
      } else {
        const detail = error.response
          ? `Fehler ${error.response.status}: ${JSON.stringify(error.response.data).slice(0, 120)}`
          : `${error.message} (${API})`;
        toast.error(`Server nicht erreichbar - ${detail}`, { duration: 15000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 bg-white">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#EFDF00] flex items-center justify-center mx-auto mb-4">
            <Car className="w-8 h-8 text-black" />
          </div>
          <CardTitle className="text-2xl font-bold text-black">
            Admin-Bereich
          </CardTitle>
          <p className="text-[#888B8D] text-sm">Auto Thurm</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-[#333333]">Passwort</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888B8D]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Admin-Passwort eingeben"
                  className="pl-10 bg-white border-[#D9D9D6] text-black"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-[#EFDF00] text-black hover:bg-[#F8EB4C] py-6 font-bold"
              data-testid="admin-login-btn"
            >
              {loading ? "Wird angemeldet..." : "Anmelden"}
            </Button>
          </form>
          <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
            <input ref={fileRef} type="file" accept=".key,application/json" className="hidden" onChange={handleKeyFile} data-testid="usb-key-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
              className="w-full py-5 border-black text-black" data-testid="usb-key-login-btn">
              <Usb className="w-4 h-4 mr-2" />
              Mit USB-Schlüssel anmelden
            </Button>
            <p className="text-xs text-[#888B8D] mt-2 text-center">Datei „AUTO_THURM_ADMIN.key“ vom Stick auswählen</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
